// The debrief room's data shapes (lane B7 S14, plan D10–D13): the panel contract the room mounts S15's panels with, over
// the generated cycle read. `CycleOut.status` carries the V1 cycle status, so the room's own status (D11) is read from
// `room_status` first, then from `status` when it is one of the room's keys, and otherwise derived from the V1 status
// and the roll.

import type { DebriefOpenOutput, MemberOut, RoomExtras } from "@/api/tools";

/** What every S15 panel takes: the room's read, the candidate tab and a re-read after a write. */
export interface RoomPanelProps {
  view: DebriefOpenOutput;
  applicationId: string;
  onChanged: () => void;
}

/** D11, the room's chain (vocab DEBRIEF_ROOM): Blocked is a branch, never a step. */
export const ROOM_CHAIN = ["not_ready", "scheduled", "roll_call", "reveal", "discussion", "alignment", "outcome_recorded"] as const;
export type RoomStep = (typeof ROOM_CHAIN)[number];
export type RoomStatus = RoomStep | "blocked";

/** One piece of evidence a note cites (notes.check_refs). */
export interface NoteSourceRef {
  scorecard_id: string;
  revision_id?: string | null;
}

/** A versioned note (D13; vocab NOTE_KINDS, NOTE_STATES): Edit, the dismissal and Correct each append a version. */
export interface NoteOut {
  note_id?: string | null;
  kind?: "summary_notes" | "debrief_preparation" | string;
  state?: "active" | "dismissed" | string;
  body?: string | null;
  version?: number | null;
  /** The submitted scorecards (and, when pinned, the revision) the note cites — the server sends each as an object. */
  source_refs?: readonly NoteSourceRef[] | null;
  author?: string | null;
  recorded_at?: string | null;
}

/** The cycle read as the room reads it: `room_status`, `blocked_reasons` and the typed `room` extras are generated. */
export type RoomView = DebriefOpenOutput;

/** One row of `required_attendees` (V1 `_out`). */
export interface Attendee {
  person_id: string;
  name: string;
  role: string;
  status: "present" | "absent_with_written_input" | "absent" | null;
  attendance_label?: string | null;
  acting_for?: string | null;
  stood_in_by?: string | null;
  required?: boolean;
  has_written_input?: boolean;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);

export function attendeesOf(view: DebriefOpenOutput): Attendee[] {
  return (view.required_attendees ?? []).map((row) => ({
    person_id: String(row.person_id ?? ""),
    name: str(row.name) ?? String(row.person_id ?? ""),
    role: str(row.role) ?? "",
    status: (str(row.status) as Attendee["status"]) ?? null,
    attendance_label: str(row.attendance_label),
    acting_for: str(row.acting_for),
    stood_in_by: str(row.stood_in_by),
    required: row.required !== false,
    has_written_input: row.has_written_input === true,
  }));
}

/** The room's extras; a reader the server sends none (own-only) reads an empty room. */
export function roomOf(view: DebriefOpenOutput): Partial<RoomExtras> {
  return view.room ?? {};
}

const isRoomStatus = (value: unknown): value is RoomStatus =>
  value === "blocked" || (ROOM_CHAIN as readonly unknown[]).includes(value);

/** D11: the room's status, read when S07 sends it, derived from the V1 cycle otherwise. */
export function roomStatusOf(view: DebriefOpenOutput): RoomStatus {
  const sent = view.room_status;
  if (isRoomStatus(sent)) return sent;
  if (isRoomStatus(view.status)) return view.status;
  const members: readonly MemberOut[] = view.members ?? [];
  switch (view.status) {
    case "decided":
    case "closed":
      return "outcome_recorded";
    case "revealed":
      return members.some((m) => (m.alignments ?? []).length > 0) ? "alignment" : "discussion";
    case "scheduled":
      return attendeesOf(view).some((a) => a.status !== null) ? "roll_call" : "scheduled";
    default:
      return "not_ready";
  }
}

/** The reveal has happened (D13: scores stay sealed until then). */
export function isRevealed(view: DebriefOpenOutput): boolean {
  if (view.revealed_at) return true;
  const status = roomStatusOf(view);
  return status === "discussion" || status === "alignment" || status === "outcome_recorded";
}

/**
 * Who is looking (plan D12/D13): a participant sees the room; the Coordinator schedules but has no vote and sees
 * status alone; the TA Manager reads after the reveal under oversight. (The Admin, his TA Admin and HR Systems Admin
 * folded into it by DC-228, reads the room as a participant.)
 */
export type Viewer = "participant" | "coordinator" | "oversight";

export function viewerOf(projection: string | null | undefined): Viewer {
  if (projection === "coordinator" || projection === "status" || projection === "schedule") return "coordinator";
  if (projection === "oversight") return "oversight";
  return "participant";
}
